import { DeleteOutlined, BarcodeOutlined, SearchOutlined, EditOutlined, PercentageOutlined, DollarOutlined, DownOutlined, FilterOutlined } from '@ant-design/icons';
import { Table, Button, Input, Space, Row, Col, InputNumber, Popconfirm, Select, Form, Modal, Dropdown, Menu, Typography, message, Checkbox } from 'antd';
import React, { useState, useEffect, useRef } from 'react';

import { ProductVariant } from '../../services/productApi';

const { Option } = Select;

interface InvoiceDetail {
  id: string;
  itemCode: string;
  quantity: number;
  unitOfMeasureCode: string;
  unitPrice: number;
  vatRate: number;
  description?: string;
  discountRate?: number;
  productDescription?: string;
  totalAmount?: number;
  discountAmount?: number;
  subtotalAmount?: number;
  vatAmount?: number;
  netAmount?: number;
  colorCode?: string;
  colorDescription?: string;
  itemDim1Code?: string;
  currencyCode?: string;
  exchangeRate?: number;
  tryEquivalent?: number; // TL karşılığı
}

interface InvoiceLinesProps {
  invoiceDetails: InvoiceDetail[];
  units: any[];
  products: any[];
  loadingProducts: boolean;
  showBarcodeModal: () => void;
  addInvoiceDetail: () => void;
  updateInvoiceDetail: (id: string, field: string, value: any) => void;
  removeInvoiceDetail: (id: string) => void;
  calculateLineAmounts: (detail: InvoiceDetail) => InvoiceDetail;
  isPriceIncludeVat: boolean;
  currencyCode?: string;
  onUpdate?: (details: InvoiceDetail[]) => void; // Toplu güncellemeler sonrası ana bileşeni bilgilendirmek için
  // form prop'unu kaldırdık çünkü artık kullanmıyoruz
}

const InvoiceLines: React.FC<InvoiceLinesProps> = ({
  invoiceDetails,
  units,
  products,
  loadingProducts,
  onUpdate,
  showBarcodeModal,
  // form prop'unu kaldırdık
  addInvoiceDetail,
  updateInvoiceDetail,
  removeInvoiceDetail,
  calculateLineAmounts,
  isPriceIncludeVat,
  currencyCode
}) => {
  // State tanımlamaları
  const [currency, setCurrency] = useState<string>(currencyCode || 'TRY');
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<'price' | 'vat' | 'discount'>('price');
  const [modalValue, setModalValue] = useState<number | null>(null);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalValueType, setModalValueType] = useState<'value' | 'percentage'>('value');
  const [applyToFiltered, setApplyToFiltered] = useState<boolean>(false);
  // Güncellenen satırların ID'lerini tutacak state
  const [updatedRows, setUpdatedRows] = useState<string[]>([]);
  
  // Toplam değerleri için state'ler
  const [editableTotalAmount, setEditableTotalAmount] = useState<number>(0);
  
  // Para birimi prop'u değiştiğinde state'i güncelle
  useEffect(() => {
    if (currencyCode && currencyCode !== currency) {
      // console.log('InvoiceLines: Para birimi değişti', currencyCode);
      setCurrency(currencyCode);
      
      // Tüm satırların para birimi kodunu güncelle
      invoiceDetails.forEach(detail => {
        if (detail.currencyCode !== currencyCode) {
          updateInvoiceDetail(detail.id, 'currencyCode', currencyCode);
          
          // Aynı zamanda birim fiyat ve tutarları da güncelle
          const updatedDetail = calculateLineAmounts({...detail, currencyCode});
          
          // Tüm güncellenmiş değerleri tek seferde uygula
          Object.keys(updatedDetail).forEach(key => {
            if (key !== 'id' && key !== 'itemCode') {
              // TypeScript tip güvenliği için key'in InvoiceDetail'in bir özelliği olduğunu doğrula
              const detailKey = key as keyof InvoiceDetail;
              updateInvoiceDetail(detail.id, detailKey, updatedDetail[detailKey]);
            }
          });
        }
      });
    }
  }, [currencyCode]);
  
  // Para birimi sembolünü belirle
  const getCurrencySymbol = (code: string) => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return code; // Bilinmeyen para birimleri için kodu göster
    }
  };
  
  // Timeout'ları saklamak için bir ref oluştur
  const timeoutsRef = useRef<{[key: string]: NodeJS.Timeout}>({});
  
  // Toplu güncelleme sırasında kullanılacak ID seti
  const updatedRowsSetRef = useRef<Set<string>>(new Set());

  // Güncelleme işlemini sarmalayan fonksiyon
  const handleUpdateInvoiceDetail = (id: string, field: string, value: any) => {
    // Güncelleme işlemini yap
    updateInvoiceDetail(id, field, value);
    
    // Eğer bu ID için bir timeout varsa temizle
    if (timeoutsRef.current[id]) {
      clearTimeout(timeoutsRef.current[id]);
    }
    
    // Eğer satır henüz güncellenenler listesinde değilse ekle
    if (!updatedRowsSetRef.current.has(id)) {
      updatedRowsSetRef.current.add(id);
      setUpdatedRows(prevRows => [...prevRows, id]);
    }
    
    // Ana bileşene güncellemeleri bildir
    // Tek satır güncellemelerinde de onUpdate'i çağır
    if (onUpdate && (field === 'quantity' || field === 'unitPrice' || field === 'vatRate' || field === 'discountRate')) {
      // Güncellenmiş fatura detaylarını oluştur
      const updatedDetails = invoiceDetails.map(detail => {
        if (detail.id === id) {
          return { ...detail, [field]: value };
        }
        return detail;
      });
      
      // Ana bileşene güncellenmiş verileri gönder
      onUpdate(updatedDetails);
    }
    
    // 3 saniye sonra güncelleme vurgusunu kaldıracak yeni bir timeout oluştur
    timeoutsRef.current[id] = setTimeout(() => {
      setUpdatedRows(prevRows => prevRows.filter(rowId => rowId !== id));
      updatedRowsSetRef.current.delete(id);
      // Timeout referansını temizle
      delete timeoutsRef.current[id];
    }, 3000);
  };
  
  const currencySymbol = getCurrencySymbol(currency);
  const [filterText, setFilterText] = useState<string>('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Ekran boyutunu kontrol et
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // İlk yükleme kontrolü
    checkMobile();
    
    // Ekran boyutu değiştiğinde kontrol et
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Filtreleme için state'ler
  const [debouncedFilterText, setDebouncedFilterText] = useState<string>('');
  
  // Filtreleme için debounce uygula
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterText(filterText);
    }, 300); // 300ms bekle
    
    return () => {
      clearTimeout(timer);
    };
  }, [filterText]);
  
  // Satırları filtrele (debounce ile)
  const filteredDetails = debouncedFilterText
    ? invoiceDetails.filter(detail => 
        (detail.itemCode?.toLowerCase() || '').includes(debouncedFilterText.toLowerCase()) ||
        ((detail.productDescription || '').toLowerCase().includes(debouncedFilterText.toLowerCase()))
      )
    : invoiceDetails;

  // Seçili satırları sil
  const removeSelectedDetails = () => {
    selectedRowKeys.forEach(id => removeInvoiceDetail(id));
    setSelectedRowKeys([]);
    message.success(`${selectedRowKeys.length} satır başarıyla silindi`);
  };
  
  // Filtrelenmiş satırları sil
  const removeFilteredDetails = () => {
    filteredDetails.forEach(detail => removeInvoiceDetail(detail.id));
    setSelectedRowKeys([]);
    message.success(`${filteredDetails.length} filtrelenmiş satır başarıyla silindi`);
  };
  
  // Toplu işlem modalını aç
  const showBulkEditModal = (type: 'price' | 'vat' | 'discount') => {
    setModalType(type);
    setModalValue(null);
    setModalValueType('value');
    setApplyToFiltered(false);
    
    switch(type) {
      case 'price':
        setModalTitle('Toplu Fiyat Güncelleme');
        break;
      case 'vat':
        setModalTitle('Toplu KDV Güncelleme');
        break;
      case 'discount':
        setModalTitle('Toplu İskonto Güncelleme');
        break;
    }
    
    setIsModalVisible(true);
  };
  
  // Modal kapatma
  const handleModalCancel = () => {
    setIsModalVisible(false);
  };
  
  // Toplu güncelleme işlemi
  const handleBulkUpdate = () => {
    if (modalValue === null) {
      message.error('Lütfen bir değer giriniz');
      return;
    }
    
    // Güncellenecek satırları belirle
    let detailsToUpdate = [];
    
    if (applyToFiltered && filterText) {
      // Filtrelenmiş satırları güncelle
      detailsToUpdate = filteredDetails;
    } else if (selectedRowKeys.length > 0) {
      // Seçili satırları güncelle
      detailsToUpdate = invoiceDetails.filter(d => selectedRowKeys.includes(d.id));
    } else {
      // Hiçbir satır seçilmemişse ve filtre uygulanmamışsa tüm satırları güncelle
      detailsToUpdate = [...invoiceDetails];
    }
    
    if (detailsToUpdate.length === 0) {
      message.error('Güncellenecek satır bulunamadı');
      return;
    }
    
    // Güncelleme başlamadan önce updatedRowsSetRef'i temizle
    updatedRowsSetRef.current.clear();
    setUpdatedRows([]);
    
    // Tüm fatura detaylarının bir kopyasını oluştur
    let updatedInvoiceDetails = [...invoiceDetails];
    
    // Değeri hesapla ve güncelle
    detailsToUpdate.forEach(detail => {
      try {
        let newValue = modalValue;
        
        // Yüzde hesaplaması
        if (modalValueType === 'percentage' && modalType === 'price') {
          newValue = detail.unitPrice * (1 + modalValue / 100);
        }
        
        // Değeri güncelle
        let updatedField = '';
        let updatedValue = 0;
        
        switch(modalType) {
          case 'price':
            updatedField = 'unitPrice';
            updatedValue = parseFloat(newValue.toFixed(2));
            break;
          case 'vat':
            updatedField = 'vatRate';
            updatedValue = parseFloat(newValue.toFixed(0));
            break;
          case 'discount':
            updatedField = 'discountRate';
            updatedValue = parseFloat(newValue.toFixed(0));
            break;
        }
        
        // Detay nesnesini güncelle
        const detailIndex = updatedInvoiceDetails.findIndex(d => d.id === detail.id);
        if (detailIndex !== -1) {
          // Önce ana alanı güncelle
          const updatedDetail = { 
            ...updatedInvoiceDetails[detailIndex], 
            [updatedField]: updatedValue 
          };
          
          // Hesaplamaları güncelle
          const calculatedDetail = calculateLineAmounts(updatedDetail);
          
          // Güncellenmiş detayı diziye ekle
          updatedInvoiceDetails[detailIndex] = calculatedDetail;
          
          // UI güncellemesi için updateInvoiceDetail'i çağır
          updateInvoiceDetail(detail.id, updatedField, updatedValue);
          
          // Hesaplanmış alanları güncelle
          const updatedFields = {
            totalAmount: calculatedDetail.totalAmount,
            discountAmount: calculatedDetail.discountAmount,
            subtotalAmount: calculatedDetail.subtotalAmount,
            vatAmount: calculatedDetail.vatAmount,
            netAmount: calculatedDetail.netAmount
          };
          
          // Tüm hesaplanmış alanları güncelle
          Object.entries(updatedFields).forEach(([field, value]) => {
            updateInvoiceDetail(detail.id, field, value);
          });
          
          // Güncellenmiş satırı işaretle
          updatedRowsSetRef.current.add(detail.id);
        }
      } catch (error) {
        console.error('Satır güncellenirken hata oluştu:', error);
      }
    });
    
    // Modal kapat ve bilgi mesajı göster
    setIsModalVisible(false);
    message.success(`${detailsToUpdate.length} satır başarıyla güncellendi`);
    setSelectedRowKeys([]);
    
    // Ana bileşene tüm güncellemelerin yansıtıldığından emin olmak için
    // onUpdate callback'ini çağır (eğer tanımlanmışsa)
    if (onUpdate) {
      // Güncellenmiş verileri ana bileşene gönder
      onUpdate(updatedInvoiceDetails);
    }
    
    // Güncellenen satırların görsel olarak vurgulanmasını sağla
    setTimeout(() => {
      const updatedIds = Array.from(updatedRowsSetRef.current);
      setUpdatedRows(updatedIds);
    }, 100);
  };

  // Mobil için stil tanımları
  const mobileStyles = {
    button: {
      fontSize: isMobile ? '12px' : '14px',
      padding: isMobile ? '0 8px' : undefined,
      height: isMobile ? '32px' : undefined
    },
    buttonContainer: {
      display: 'flex' as const,
      gap: '8px',
      marginBottom: '8px',
      flexWrap: 'wrap' as const
    },
    tableContainer: {
      overflowX: 'auto' as const
    }
  };
  
  // Mobil tablo için CSS stillerini ekliyoruz
  useEffect(() => {
    if (isMobile) {
      // Stil elementi oluştur
      const styleEl = document.createElement('style');
      styleEl.id = 'mobile-invoice-table-styles';
      styleEl.innerHTML = `
        .mobile-table-row td {
          padding: 8px 4px !important;
          border-bottom: 2px solid #fa8c16 !important;
        }
        .mobile-table-row:hover td {
          background-color: #fff7e6 !important;
        }
        .mobile-table-row {
          margin-bottom: 12px !important;
        }
        .ant-table-tbody > tr.mobile-table-row > td {
          transition: background 0.2s, border-color 0.2s;
        }
        .ant-table-tbody > tr.mobile-table-row:hover > td {
          background-color: #fff7e6 !important;
        }
        .kdv-highlight {
          color: #1890ff;
          font-weight: bold;
        }
        .updated-row td {
          background-color: #e6f7ff !important;
          transition: background-color 0.5s ease;
          animation: pulse 1.5s ease-in-out;
        }
        .updated-row:hover td {
          background-color: #bae7ff !important;
        }
        @keyframes pulse {
          0% { background-color: #e6f7ff; }
          50% { background-color: #91d5ff; }
          100% { background-color: #e6f7ff; }
        }
      `;
      document.head.appendChild(styleEl);
      
      // Temizleme fonksiyonu
      return () => {
        const existingStyle = document.getElementById('mobile-invoice-table-styles');
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    }
  }, [isMobile]);

  // Tablo sütunları
  // Mobil ve masaüstü için farklı kolon yapıları tanımlıyoruz
  const mobileColumns = [
    {
      title: '',
      dataIndex: 'itemCode',
      key: 'productInfo',
      width: '40%',
      render: (_: any, record: InvoiceDetail) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 0' }}>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{record.itemCode}</div>
          <div style={{ fontSize: '13px' }}>{record.productDescription || record.description || '-'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.colorDescription ? `${record.colorDescription} ` : ''}
            {record.itemDim1Code ? record.itemDim1Code : ''}
          </div>
        </div>
      )
    },
    {
      title: '',
      dataIndex: 'quantity',
      key: 'quantityPrice',
      width: '25%',
      render: (_: any, record: InvoiceDetail) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 0' }}>
          <div>
            <InputNumber
              value={record.quantity}
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              onChange={(value) => {
                if (value !== null) {
                  // First update the quantity
                  handleUpdateInvoiceDetail(record.id, 'quantity', value);
                  
                  // Then calculate and update all dependent values
                  const updatedDetail = {
                    ...record,
                    quantity: value
                  };
                  const calculatedDetail = calculateLineAmounts(updatedDetail);
                  
                  // Update calculated fields
                  handleUpdateInvoiceDetail(record.id, 'totalAmount', calculatedDetail.totalAmount);
                  handleUpdateInvoiceDetail(record.id, 'discountAmount', calculatedDetail.discountAmount);
                  handleUpdateInvoiceDetail(record.id, 'subtotalAmount', calculatedDetail.subtotalAmount);
                  handleUpdateInvoiceDetail(record.id, 'vatAmount', calculatedDetail.vatAmount);
                  handleUpdateInvoiceDetail(record.id, 'netAmount', calculatedDetail.netAmount);
                }
              }}
            />
          </div>
          <div style={{ marginTop: '4px' }}>
            <InputNumber
              value={record.unitPrice}
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
              onChange={(value) => {
                if (value !== null) {
                  // First update the unit price
                  handleUpdateInvoiceDetail(record.id, 'unitPrice', value);
                  
                  // Then calculate and update all dependent values
                  const updatedDetail = {
                    ...record,
                    unitPrice: value
                  };
                  const calculatedDetail = calculateLineAmounts(updatedDetail);
                  
                  // Update calculated fields
                  handleUpdateInvoiceDetail(record.id, 'totalAmount', calculatedDetail.totalAmount);
                  handleUpdateInvoiceDetail(record.id, 'discountAmount', calculatedDetail.discountAmount);
                  handleUpdateInvoiceDetail(record.id, 'subtotalAmount', calculatedDetail.subtotalAmount);
                  handleUpdateInvoiceDetail(record.id, 'vatAmount', calculatedDetail.vatAmount);
                  handleUpdateInvoiceDetail(record.id, 'netAmount', calculatedDetail.netAmount);
                }
              }}
            />
          </div>
        </div>
      )
    },
    {
      title: '',
      dataIndex: 'vatRate',
      key: 'vatTotal',
      width: '25%',
      render: (_: any, record: InvoiceDetail) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 0' }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1890ff', marginBottom: '2px' }}>
              KDV (%)
            </div>
            <InputNumber
              value={record.vatRate}
              min={0}
              max={100}
              step={1}
              precision={0}
              style={{ width: '100%' }}
              formatter={value => `${value}%`}
              parser={(value) => parseFloat(value!.replace('%', ''))}
              onChange={(value) => {
                if (value !== null) {
                  // First update the VAT rate
                  handleUpdateInvoiceDetail(record.id, 'vatRate', value);
                  
                  // Then calculate and update all dependent values
                  const updatedDetail = {
                    ...record,
                    vatRate: value
                  };
                  const calculatedDetail = calculateLineAmounts(updatedDetail);
                  
                  // Update calculated fields
                  handleUpdateInvoiceDetail(record.id, 'totalAmount', calculatedDetail.totalAmount);
                  handleUpdateInvoiceDetail(record.id, 'discountAmount', calculatedDetail.discountAmount);
                  handleUpdateInvoiceDetail(record.id, 'subtotalAmount', calculatedDetail.subtotalAmount);
                  handleUpdateInvoiceDetail(record.id, 'vatAmount', calculatedDetail.vatAmount);
                  handleUpdateInvoiceDetail(record.id, 'netAmount', calculatedDetail.netAmount);
                }
              }}
            />
          </div>
          <div style={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'right', marginTop: '4px' }}>
            {record.netAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(record.currencyCode || currency)}
          </div>
        </div>
      )
    },
    {
      title: '',
      key: 'action',
      width: '10%',
      render: (_: any, record: InvoiceDetail) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '8px 0' }}>
          <Popconfirm
            title="Bu satırı silmek istediğinize emin misiniz?"
            onConfirm={() => removeInvoiceDetail(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="link" danger icon={<DeleteOutlined style={{ fontSize: '18px' }} />} />
          </Popconfirm>
        </div>
      )
    }
  ];

  // Masaüstü için normal kolonlar
  const desktopColumns = [
    {
      title: 'Ürün Bilgileri',
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 200,
      render: (text: string, record: InvoiceDetail) => (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '4px'
        }}>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
            {record.itemCode || '-'}
          </div>
          
          <div>
            <Input
              value={record.productDescription || ''}
              onChange={e => handleUpdateInvoiceDetail(record.id, 'description', e.target.value)}
              placeholder="Açıklama"
              size="small"
              style={{ padding: '2px 0' }}
              bordered={false}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#666' }}>
            {record.colorDescription && (
              <div>{record.colorDescription}</div>
            )}
            {record.itemDim1Code && (
              <div>{record.itemDim1Code}</div>
            )}
          </div>
        </div>
      )
    },
    {
      title: 'Mik',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 50,
      render: (text: number, record: InvoiceDetail) => (
        <InputNumber
          value={text}
          min={0}
          step={0.01}
          precision={2}
          style={{ width: '100%' }}
          onChange={(value) => {
            if (value !== null) {
              // First update the quantity
              handleUpdateInvoiceDetail(record.id, 'quantity', value);
              
              // Then calculate and update all dependent values
              const updatedDetail = {
                ...record,
                quantity: value
              };
              const calculatedDetail = calculateLineAmounts(updatedDetail);
              
              // Update calculated fields
              handleUpdateInvoiceDetail(record.id, 'totalAmount', calculatedDetail.totalAmount);
              handleUpdateInvoiceDetail(record.id, 'discountAmount', calculatedDetail.discountAmount);
              handleUpdateInvoiceDetail(record.id, 'subtotalAmount', calculatedDetail.subtotalAmount);
              handleUpdateInvoiceDetail(record.id, 'vatAmount', calculatedDetail.vatAmount);
              handleUpdateInvoiceDetail(record.id, 'netAmount', calculatedDetail.netAmount);
            }
          }}
        />
      )
    },
    {
      title: 'Brm',
      dataIndex: 'unitOfMeasureCode',
      key: 'unitOfMeasureCode',
      width: 50,
      render: (text: string, record: InvoiceDetail) => (
        <Select
          value={text}
          style={{ width: '100%' }}
          onChange={(value) => updateInvoiceDetail(record.id, 'unitOfMeasureCode', value)}
        >
          {units.map(unit => <Option key={unit.code} value={unit.code}>{unit.code}</Option>)}
        </Select>
      )
    },
    {
      title: `Fiyat`,
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 60,
      render: (text: number, record: InvoiceDetail) => (
        <InputNumber
          value={text}
          min={0}
          step={0.01}
          precision={2}
          style={{ width: '100%' }}
          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
          onChange={(value) => {
            if (value !== null) {
              // First update the unit price
              handleUpdateInvoiceDetail(record.id, 'unitPrice', value);
              
              // Then calculate and update all dependent values
              const updatedDetail = {
                ...record,
                unitPrice: value
              };
              const calculatedDetail = calculateLineAmounts(updatedDetail);
              
              // Update calculated fields
              handleUpdateInvoiceDetail(record.id, 'totalAmount', calculatedDetail.totalAmount);
              handleUpdateInvoiceDetail(record.id, 'discountAmount', calculatedDetail.discountAmount);
              handleUpdateInvoiceDetail(record.id, 'subtotalAmount', calculatedDetail.subtotalAmount);
              handleUpdateInvoiceDetail(record.id, 'vatAmount', calculatedDetail.vatAmount);
              handleUpdateInvoiceDetail(record.id, 'netAmount', calculatedDetail.netAmount);
            }
          }}
        />
      )
    },
    {
      title: 'İsk',
      dataIndex: 'discountRate',
      key: 'discountRate',
      width: 40,
      render: (text: number, record: InvoiceDetail) => (
        <InputNumber
          value={text || 0}
          min={0}
          max={100}
          step={1}
          precision={0}
          style={{ width: '100%' }}
          formatter={value => `${value}%`}
          parser={(value) => parseFloat(value!.replace('%', ''))}
          onChange={(value) => {
            if (value !== null) {
              // First update the discount rate
              handleUpdateInvoiceDetail(record.id, 'discountRate', value);
              
              // Then calculate and update all dependent values
              const updatedDetail = {
                ...record,
                discountRate: value
              };
              const calculatedDetail = calculateLineAmounts(updatedDetail);
              
              // Update calculated fields
              handleUpdateInvoiceDetail(record.id, 'totalAmount', calculatedDetail.totalAmount);
              handleUpdateInvoiceDetail(record.id, 'discountAmount', calculatedDetail.discountAmount);
              handleUpdateInvoiceDetail(record.id, 'subtotalAmount', calculatedDetail.subtotalAmount);
              handleUpdateInvoiceDetail(record.id, 'vatAmount', calculatedDetail.vatAmount);
              handleUpdateInvoiceDetail(record.id, 'netAmount', calculatedDetail.netAmount);
            }
          }}
        />
      )
    },
    {
      title: 'KDV',
      dataIndex: 'vatRate',
      key: 'vatRate',
      width: 40,
      render: (text: number, record: InvoiceDetail) => (
        <InputNumber
          value={text}
          min={0}
          max={100}
          step={1}
          precision={0}
          style={{ width: '100%' }}
          formatter={value => `${value}%`}
          parser={(value) => parseFloat(value!.replace('%', ''))}
          onChange={(value) => {
            if (value !== null) {
              // First update the VAT rate
              handleUpdateInvoiceDetail(record.id, 'vatRate', value);
              
              // Then calculate and update all dependent values
              const updatedDetail = {
                ...record,
                vatRate: value
              };
              const calculatedDetail = calculateLineAmounts(updatedDetail);
              
              // Update calculated fields
              handleUpdateInvoiceDetail(record.id, 'totalAmount', calculatedDetail.totalAmount);
              handleUpdateInvoiceDetail(record.id, 'discountAmount', calculatedDetail.discountAmount);
              handleUpdateInvoiceDetail(record.id, 'subtotalAmount', calculatedDetail.subtotalAmount);
              handleUpdateInvoiceDetail(record.id, 'vatAmount', calculatedDetail.vatAmount);
              handleUpdateInvoiceDetail(record.id, 'netAmount', calculatedDetail.netAmount);
            }
          }}
        />
      )
    },
    {
      title: `Top`,
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 60,
      render: (text: number, record: InvoiceDetail) => (
        <span style={{ fontWeight: 'bold' }}>
          {text?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(record.currencyCode || currency)}
        </span>
      )
    },
    {
      title: `Net`,
      dataIndex: 'netAmount',
      key: 'netAmount',
      width: 60,
      render: (text: number, record: InvoiceDetail) => (
        <span style={{ fontWeight: 'bold' }}>
          {text?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(record.currencyCode || currency)}
        </span>
      )
    },
    {
      title: `TL Karşılığı`,
      dataIndex: 'tryEquivalent',
      key: 'tryEquivalent',
      width: 70,
      render: (_: any, record: InvoiceDetail) => {
        // Eğer para birimi TRY ise, aynı değeri göster
        if (record.currencyCode === 'TRY' || !record.exchangeRate) {
          return <span>-</span>;
        }
        
        // TL karşılığını hesapla (net tutar * kur)
        const tryValue = (record.netAmount || 0) * (record.exchangeRate || 1);
        
        return (
          <span style={{ color: '#1890ff' }}>
            {tryValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
          </span>
        );
      }
    },
    {
      title: '',
      key: 'action',
      width: 40,
      render: (_: any, record: InvoiceDetail) => (
        <Popconfirm
          title="Bu satırı silmek istediğinize emin misiniz?"
          onConfirm={() => removeInvoiceDetail(record.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
];

return (
  <div className="invoice-lines">
    {/* Form bileşenini kaldırdık, form instance'ını sadece değerleri okumak için kullanıyoruz */}
    <div>
      <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
        <Col span={24}>
          <div style={mobileStyles.buttonContainer}>
            <Button 
              type="primary" 
              icon={<BarcodeOutlined />} 
              onClick={showBarcodeModal}
              style={mobileStyles.button}
            >
              Satır Ekle
            </Button>
            
            {/* Toplu İşlemler Dropdown Menüsü */}
            <Dropdown overlay={
              <Menu>
                <Menu.Item key="bulkPrice" onClick={() => showBulkEditModal('price')} icon={<DollarOutlined />}>
                  Toplu Fiyat Güncelle
                </Menu.Item>
                <Menu.Item key="bulkVat" onClick={() => showBulkEditModal('vat')} icon={<PercentageOutlined />}>
                  Toplu KDV Güncelle
                </Menu.Item>
                <Menu.Item key="bulkDiscount" onClick={() => showBulkEditModal('discount')} icon={<PercentageOutlined />}>
                  Toplu İskonto Güncelle
                </Menu.Item>
                <Menu.Divider />
                {selectedRowKeys.length > 0 && (
                  <Menu.Item danger key="deleteSelected" onClick={() => {
                    Modal.confirm({
                      title: 'Seçili Satırları Sil',
                      content: `${selectedRowKeys.length} satırı silmek istediğinize emin misiniz?`,
                      okText: 'Evet',
                      cancelText: 'Hayır',
                      onOk: removeSelectedDetails
                    });
                  }} icon={<DeleteOutlined />}>
                    Seçili Satırları Sil ({selectedRowKeys.length})
                  </Menu.Item>
                )}
                {filterText && filteredDetails.length > 0 && (
                  <Menu.Item danger key="deleteFiltered" onClick={() => {
                    Modal.confirm({
                      title: 'Filtrelenmiş Satırları Sil',
                      content: `${filteredDetails.length} filtrelenmiş satırı silmek istediğinize emin misiniz?`,
                      okText: 'Evet',
                      cancelText: 'Hayır',
                      onOk: removeFilteredDetails
                    });
                  }} icon={<DeleteOutlined />}>
                    Filtrelenmiş Satırları Sil ({filteredDetails.length})
                  </Menu.Item>
                )}
              </Menu>
            } trigger={['click']}>
              <Button style={mobileStyles.button}>
                Toplu İşlemler <DownOutlined />
              </Button>
            </Dropdown>
            
            {/* Seçili Satırları Silme Butonu */}
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`${selectedRowKeys.length} satırı silmek istediğinize emin misiniz?`}
                onConfirm={removeSelectedDetails}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                  style={mobileStyles.button}
                >
                  Seçilenleri Sil ({selectedRowKeys.length})
                </Button>
              </Popconfirm>
            )}
          </div>
        </Col>
        <Col span={24}>
          <Input
            placeholder="Ürün kodu veya açıklaması ile filtrele"
            prefix={<SearchOutlined />}
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            allowClear
            addonAfter={
              filterText && filteredDetails.length > 0 ? (
                <Typography.Text style={{ fontSize: '12px' }}>
                  <FilterOutlined /> {filteredDetails.length} sonuç
                </Typography.Text>
              ) : null
            }
          />
        </Col>
      </Row>

      <div style={mobileStyles.tableContainer}>
        <Table
          dataSource={filteredDetails}
          columns={isMobile ? mobileColumns : desktopColumns}
          rowKey="id"
          size={isMobile ? "middle" : "small"}
          scroll={{ x: '100%', y: 300 }}
          pagination={false}
          rowClassName={(record) => {
            let className = isMobile ? 'mobile-table-row' : '';
            if (updatedRows.includes(record.id)) {
              className += ' updated-row';
            }
            return className;
          }}
          showHeader={!isMobile}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys as string[]),
          }}
          summary={(pageData) => {
            // Hesaplanan toplamlar
            let totalQuantity = 0;
            let totalAmount = 0;
            let totalNetAmount = 0;

            // Toplam değerleri hesapla
            pageData.forEach(({ quantity, totalAmount: lineTotal, netAmount }) => {
              // quantity değeri string veya number olabilir, güvenli bir şekilde parse et
              const itemQuantity = typeof quantity === 'string' 
                ? parseFloat(quantity) || 0 
                : (quantity || 0);
              totalQuantity += itemQuantity;
              totalAmount += lineTotal || 0;
              totalNetAmount += netAmount || 0;
            });
            
            // Toplam tutar ilk kez hesaplandığında veya değiştiğinde state'e aktar
            if (totalAmount > 0 && editableTotalAmount === 0) {
              setEditableTotalAmount(totalAmount);
            }
            
            // Eğer düzenlenebilir toplam tutar henüz ayarlanmamışsa, hesaplanan tutarı kullan
            const displayTotalAmount = editableTotalAmount || totalAmount;
            
            // Toplam tutarların düzenlenmesi sonrasında fatura satırlarını güncelle
            const handleTotalAmountChange = (newTotalAmount: number | null) => {
              if (newTotalAmount === null) return;
              
              setEditableTotalAmount(newTotalAmount);
              
              // Toplam tutar değiştiğinde, tüm satırlara oranında yansıt
              if (totalAmount > 0) {
                const ratio = newTotalAmount / totalAmount;
                
                // Tüm satırları güncelle
                const updatedDetails = invoiceDetails.map(detail => {
                  const newUnitPrice = detail.unitPrice * ratio;
                  return {
                    ...detail,
                    unitPrice: parseFloat(newUnitPrice.toFixed(2))
                  };
                });
                
                // Ana bileşene güncellenmiş verileri gönder
                if (onUpdate) {
                  onUpdate(updatedDetails);
                }
              }
            };

            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <strong>Toplam</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <strong>
                      {typeof totalQuantity === 'number' ? totalQuantity.toFixed(2) : '0.00'}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} colSpan={6} />
                  <Table.Summary.Cell index={7}>
                    <strong>
                      <InputNumber
                        value={displayTotalAmount}
                        min={0}
                        step={0.01}
                        precision={2}
                        style={{ width: '100%' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
                        onChange={handleTotalAmountChange}
                        addonAfter={currencySymbol}
                      />
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8}>
                    <strong>
                      {totalNetAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currencySymbol}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </div>
    </div>
      {/* Toplu Güncelleme Modalı */}
      <Modal
        title={modalTitle}
        open={isModalVisible}
        onCancel={handleModalCancel}
        onOk={handleBulkUpdate}
        okText="Uygula"
        cancelText="İptal"
      >
        <Form layout="vertical">
          <Form.Item label="Değer Tipi">
            <Select 
              value={modalValueType} 
              onChange={(value) => setModalValueType(value as 'value' | 'percentage')}
              style={{ width: '100%' }}
            >
              {modalType === 'price' ? (
                <>
                  <Select.Option value="value">Sabit Değer</Select.Option>
                  <Select.Option value="percentage">Yüzde Değişim (%)</Select.Option>
                </>
              ) : (
                <Select.Option value="value">Yüzde Değeri (%)</Select.Option>
              )}
            </Select>
          </Form.Item>
          
          <Form.Item label={modalValueType === 'percentage' ? 'Yüzde Değeri' : 'Yeni Değer'}>
            <InputNumber
              value={modalValue}
              onChange={(value) => setModalValue(value)}
              style={{ width: '100%' }}
              min={modalType === 'discount' || modalType === 'vat' ? 0 : undefined}
              max={modalType === 'discount' || modalType === 'vat' ? 100 : undefined}
              precision={modalType === 'price' ? 2 : 0}
              formatter={modalValueType === 'percentage' || modalType === 'discount' || modalType === 'vat' ? 
                (value) => `${value}%` : undefined}
              parser={modalValueType === 'percentage' || modalType === 'discount' || modalType === 'vat' ? 
                (value) => parseFloat(value!.replace('%', '')) : undefined}
            />
          </Form.Item>
          
          <Form.Item>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Typography.Text type="secondary">
                {selectedRowKeys.length > 0 ? 
                  `${selectedRowKeys.length} satır seçildi` : 
                  'Hiç satır seçilmedi'}
              </Typography.Text>
              
              {filterText && (
                <Typography.Text type="secondary">
                  {filteredDetails.length} satır filtrelendi
                </Typography.Text>
              )}
              
              <div>
                <Checkbox 
                  checked={applyToFiltered} 
                  onChange={(e: any) => setApplyToFiltered(e.target.checked)}
                  disabled={!filterText || filteredDetails.length === 0}
                >
                  Sadece filtrelenmiş satırlara uygula
                </Checkbox>
              </div>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InvoiceLines;